import { Button, Flex, message, Popconfirm, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import CreatePage from './CreatePage';
import EditPage from './EditPage';
import { PlusOutlined } from '@ant-design/icons';
import { PageDto } from '../../backendapi/requests';
import { usePageServiceDeleteBackendv2Page, usePageServiceGetBackendv2Page } from '../../backendapi/queries';
import { ConfigContext } from '../../contexts/ConfigContext';
import { Filter } from '../../types/types';
import { GenerateODataFilter } from '../../helpers/odataFunctions';


const MaterialList: React.FC = () => {

    const { currentClient } = useContext(ConfigContext);
    
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedPage, setSelectedPage] = useState<PageDto | undefined>(undefined);

    const [createOpen, setCreateOpen] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

    const filters = useMemo(() => {
        let filters: Filter[] = [];    
        
        if (currentClient) {
            filters.push({ type: 'select', values: [{value: currentClient.id}], property: 'clientId', name: 'clientId' });
        }

        return filters;
    }, [currentClient]);

    const { data, isFetching, refetch } = usePageServiceGetBackendv2Page({
        clientId: currentClient.id,
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: (prev) => prev });
    const { mutateAsync: deletePage } = usePageServiceDeleteBackendv2Page();


    const openCreate = () => {
        setCreateOpen(true);
    };

    const handleCreateClose = useCallback(() => {
        setCreateOpen(false);
        refetch();
    }, [refetch]);

    const handleEditClose = useCallback(() => {
        setSelectedPage(undefined);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<PageDto> | SorterResult<PageDto>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((page: PageDto) => {
        if (page.id) {
            deletePage({ key: page.id }).then(() => {
                messageApi.success("Page deleted");
                refetch();
            })
        }
    }, [deletePage, messageApi, refetch]);

    const columns: ColumnsType<PageDto> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => setSelectedPage(record)}>Edit</Button>
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}

            <Flex justify='flex-end' style={{ marginBottom: 16 }}>
                <Space>
                    <Button type="primary" onClick={openCreate} icon={<PlusOutlined />}>
                        New page
                    </Button>
                </Space>
            </Flex>
            
            <CreatePage isOpen={createOpen} onClose={handleCreateClose} />

            <EditPage isOpen={selectedPage != null} onClose={handleEditClose} page={selectedPage} />

            <Table
                loading={isFetching}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />
        </>
    );
};

export default MaterialList;